import { ReactNode, useState, useRef, useEffect, Children } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ResizeIcon from "assets/icons/ResizeIcon";
import AccordianHeader from "./AccordianHeader";
import { RightPaneMetrics } from "types";
import classNames from "classnames";
import {
  AccordionProvider,
  useAccordionContext,
} from "contexts/AccordionContext";

import styles from "./style.module.scss";

type VLAccordionTypes = {
  id?: string;
  title: string;
  className?: string;
  icon?: JSX.Element;
  inSearch?: boolean;
  children: ReactNode;
  isLoading?: boolean;
  allowResize?: boolean;
  allowOverflow?: boolean;
  initiallyClosed?: boolean;
  selectedMetric?: RightPaneMetrics | null;
  onToggleMetrics?: () => void;
};

type AccordionContentProps = {
  id?: string;
  children: ReactNode;
  isExpanded: boolean;
  allowResize?: boolean;
};

const AccordionContent = ({
  id,
  children,
  isExpanded,
  allowResize,
}: AccordionContentProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const { height, minHeight, maxHeight, setHeight } = useAccordionContext();
  const showResizeHandle = allowResize && !(height < minHeight);

  useEffect(() => {
    if (id && allowResize) {
      const savedHeight = localStorage.getItem(`accordion-height-${id}`);
      if (savedHeight) setHeight(parseInt(savedHeight));
    }
    //eslint-disable-next-line
  }, []);

  const handleResize = (e: React.MouseEvent) => {
    if (!contentRef.current) return;

    const startY = e.clientY;
    const startHeight = contentRef.current.offsetHeight;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaY = e.clientY - startY;
      const newHeight = startHeight + deltaY;
      setHeight(newHeight);
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.classList.remove("resizing");

      if (id && contentRef.current) {
        localStorage.setItem(
          `accordion-height-${id}`,
          contentRef.current.offsetHeight.toString()
        );
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.classList.add("resizing");
  };

  return (
    <AnimatePresence initial={false}>
      {isExpanded && (
        <motion.div
          key="content"
          initial={{ height: 0, opacity: 0 }}
          animate={{
            height: allowResize && height ? height : "unset",
            opacity: 1,
          }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
        >
          <div
            className={styles.accordianContent}
            ref={contentRef}
            style={allowResize ? { minHeight, maxHeight } : undefined}
          >
            {children}
          </div>
          {showResizeHandle && (
            <div className={styles.resizeHandle} onMouseDown={handleResize}>
              <ResizeIcon />
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const VLAccordian = ({
  id,
  title,
  icon,
  children,
  inSearch,
  className,
  initiallyClosed,
  isLoading = false,
  selectedMetric = null,
  allowOverflow = false,
  allowResize,
  onToggleMetrics = () => {},
}: VLAccordionTypes) => {
  const initialIsExpanded = !!inSearch || !initiallyClosed;
  const [isExpanded, setIsExpanded] = useState(initialIsExpanded);
  const containerRef = useRef<HTMLDivElement>(null);

  const firstChildClassName =
    (Children.toArray(children)[0] as JSX.Element)?.props?.className || "";
  const showAllowSize =
    allowResize &&
    !inSearch &&
    !firstChildClassName.toLowerCase().includes("loadingbox");

  const filterClassnames = classNames(styles.rightPaneFilter, className, {
    [styles.closedFilter]: !isExpanded,
    [styles.allowOverflow]: allowOverflow,
  });

  const toggleAccordion = () => {
    setIsExpanded(!isExpanded);
  };

  const handleOutsideClick = (e: MouseEvent) => {
    if (!containerRef.current?.contains(e.target as Node)) {
      setIsExpanded(false);
    }
  };

  useEffect(() => {
    if (inSearch) {
      setIsExpanded(true);
      document.addEventListener("click", handleOutsideClick);
    } else {
      document.removeEventListener("click", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [inSearch, isExpanded]);

  return (
    <AccordionProvider>
      <motion.div
        className={filterClassnames}
        whileHover="hover"
        ref={containerRef}
      >
        <AccordianHeader
          icon={icon}
          title={title}
          isExpanded={isExpanded}
          selectedMetric={selectedMetric}
          isLoading={isExpanded && isLoading}
          onToggle={toggleAccordion}
          onToggleMetrics={onToggleMetrics}
        />
        <AccordionContent
          id={id}
          isExpanded={isExpanded}
          allowResize={showAllowSize}
        >
          {children}
        </AccordionContent>
      </motion.div>
    </AccordionProvider>
  );
};

export default VLAccordian;
